import React, { useState, useEffect, useMemo, useCallback } from "react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  FaUserPlus,
  FaEnvelope,
  FaPhone,
  FaWhatsapp,
  FaUser,
  FaBuilding,
  FaTrophy,
  FaExclamationCircle,
  FaCalculator,
  FaFilePdf,
} from "react-icons/fa";
import { db, functions, auth } from "../../firebase";
import { useToast } from "../../components/Toast";
import Loader from "../../components/Loader";
import EmptyState from "../../components/EmptyState";
import {
  colors,
  spacing,
  typography,
  radius,
  pageContainer,
  pageTitle,
  pageSubtitle,
} from "../../utils/designTokens";

/**
 * Pantalla CRM de Leads (SuperAdmin).
 *
 * Lista todos los leads (demos web + prospectos de WhatsApp + manuales) y permite
 * cambiar su estado, asignar comerciales y dejar notas internas. Lee en tiempo real
 * de la coleccion `leads`.
 *
 * Hoy escribe directo a Firestore (status/notas); cuando se necesite validacion de
 * permisos mas estricta, mover a la callable `updateLeadStatus`.
 */

// Estados del embudo
const STATUSES = [
  { id: "new", label: "Nuevo", color: colors.primary, bg: colors.primaryLighter },
  { id: "contacted", label: "Contactado", color: colors.warning, bg: colors.warningLight },
  { id: "demo_in_progress", label: "Demo activa", color: colors.info, bg: colors.infoLight },
  { id: "won", label: "Ganado", color: colors.success, bg: colors.successLight },
  { id: "lost", label: "Perdido", color: colors.gray600, bg: colors.gray100 },
];

// Estados auxiliares (no se muestran como tabs principales pero pueden aparecer en datos)
const AUX_STATUSES = {
  demo_loaded: { label: "Entró a la demo", color: colors.primary, bg: colors.primaryLighter },
  waitlist: { label: "Lista de espera", color: colors.gray700, bg: colors.gray100 },
  existing_user: { label: "Ya tenía cuenta", color: colors.gray700, bg: colors.gray100 },
  quoting: { label: "Cotizando", color: colors.info, bg: colors.infoLight },
  quoted: { label: "Cotizado", color: colors.info, bg: colors.infoLight },
  quote_failed: { label: "Error al cotizar", color: colors.error, bg: colors.errorLight },
  quote_send_failed: { label: "Cotización sin enviar", color: colors.error, bg: colors.errorLight },
};

const SOURCES = {
  demo_signup: { label: "Demo web", icon: FaUserPlus, color: colors.primary },
  landing_form: { label: "Form de landing", icon: FaEnvelope, color: colors.warning },
  whatsapp_inbound: { label: "WhatsApp", icon: FaWhatsapp, color: colors.success },
  cotizador_web: { label: "Cotizador web", icon: FaCalculator, color: colors.info },
  manual: { label: "Manual", icon: FaUser, color: colors.gray600 },
};

const ROLES = {
  residente: "Residente",
  admin_edificio: "Admin de edificio",
  desarrollador: "Desarrollador",
};

const PUBLICOS = {
  edificios: "Edificios",
  administradoras: "Administradoras",
  desarrolladoras: "Desarrolladoras",
};

// SLA de primer contacto: horas sin respuesta humana para un lead que espera.
// `contactedAt` lo setea solo el backend cuando alguien le responde de verdad.
const horasSinContactar = (lead) => {
  if (!lead || lead.contactedAt) return null;
  if (!["new", "demo_loaded"].includes(lead.status || "new")) return null;
  const created = lead.createdAt?.toDate ? lead.createdAt.toDate() : null;
  if (!created) return null;
  return Math.floor((Date.now() - created.getTime()) / (60 * 60 * 1000));
};

const Leads = () => {
  const { showToast } = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filterSource, setFilterSource] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPublico, setFilterPublico] = useState("all");
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(false);
  const [newNote, setNewNote] = useState("");

  // Carga en tiempo real
  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, "leads"), orderBy("updatedAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLeads(data);
        setLoading(false);
      },
      (err) => {
        // Si falta el indice, mensaje claro
        console.error("leads onSnapshot error:", err);
        const indexHint = err.message?.includes("requires an index")
          ? " (puede faltar un índice en Firestore — ver consola)"
          : "";
        showToast(`Error cargando leads${indexHint}`, "error");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [showToast]);

  // Filtros + busqueda
  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      if (filterSource !== "all" && l.source !== filterSource) return false;
      if (filterStatus !== "all" && l.status !== filterStatus) return false;
      if (filterPublico !== "all" && (l.publico || "") !== filterPublico) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = [l.name, l.email, l.phone, l.edificio]
          .filter(Boolean)
          .map((x) => String(x).toLowerCase())
          .join(" ");
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [leads, filterSource, filterStatus, filterPublico, search]);

  // Lead seleccionado (lookup en vivo para reflejar cambios de onSnapshot)
  const selected = useMemo(
    () => leads.find((l) => l.id === selectedId) || null,
    [leads, selectedId]
  );

  // Cambiar status (callable; las rules tienen leads write-only-backend)
  const changeStatus = useCallback(
    async (newStatus) => {
      if (!selected || updating) return;
      setUpdating(true);
      try {
        const fn = httpsCallable(functions, "updateLeadStatus");
        await fn({ leadId: selected.id, status: newStatus });
        showToast(
          `Estado actualizado a "${STATUSES.find((s) => s.id === newStatus)?.label || newStatus}"`,
          "success"
        );
      } catch (e) {
        console.error("changeStatus error:", e);
        showToast(e.message || "Error actualizando estado", "error");
      } finally {
        setUpdating(false);
      }
    },
    [selected, updating, showToast]
  );

  // Agregar nota (callable)
  const addNote = useCallback(async () => {
    const text = (newNote || "").trim();
    if (!text || !selected || updating) return;
    setUpdating(true);
    try {
      const fn = httpsCallable(functions, "addLeadNote");
      await fn({ leadId: selected.id, text });
      setNewNote("");
      showToast("Nota agregada", "success");
    } catch (e) {
      console.error("addNote error:", e);
      showToast(e.message || "Error agregando nota", "error");
    } finally {
      setUpdating(false);
    }
  }, [newNote, selected, updating, showToast]);

  // Asignar / liberar el lead (callable; email vacio libera)
  const assignLead = useCallback(
    async (email) => {
      if (!selected || updating) return;
      setUpdating(true);
      try {
        const fn = httpsCallable(functions, "assignLead");
        await fn({ leadId: selected.id, email: email || "" });
        showToast(email ? "Lead asignado" : "Lead liberado", "success");
      } catch (e) {
        console.error("assignLead error:", e);
        showToast(e.message || "Error asignando lead", "error");
      } finally {
        setUpdating(false);
      }
    },
    [selected, updating, showToast]
  );

  // Conteos rapidos ("nuevos" incluye demo_loaded: entro a la demo y nadie lo contacto)
  const counts = useMemo(
    () => ({
      total: leads.length,
      new: leads.filter((l) => ["new", "demo_loaded"].includes(l.status) || !l.status).length,
      sinContactar: leads.filter((l) => (horasSinContactar(l) ?? 0) >= 24).length,
      needsHuman: leads.filter((l) => l.needsHuman).length,
    }),
    [leads]
  );

  if (loading) return <Loader />;

  return (
    <div style={pageContainer}>
      {/* Header */}
      <div style={{ marginBottom: spacing[4] }}>
        <h1 style={pageTitle}>
          <FaUserPlus style={{ marginRight: 8, color: colors.primary }} /> Leads
        </h1>
        <p style={{ ...pageSubtitle, maxWidth: 720, marginTop: 4 }}>
          Personas interesadas en Habitia (demos web + WhatsApp + manuales). Asigná, contactá y moveelos por el embudo.
        </p>
      </div>

      {/* Filtros + busqueda */}
      <div style={{ display: "flex", gap: spacing[3], marginBottom: spacing[3], flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder="Buscar por nombre, email, teléfono..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 240,
            padding: "8px 12px",
            border: `1px solid ${colors.gray300}`,
            borderRadius: radius.sm,
            fontFamily: "inherit",
            fontSize: typography.sizes.base,
            fontWeight: 400,
          }}
        />
        <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} style={selectStyle}>
          <option value="all">Todos los orígenes</option>
          {Object.entries(SOURCES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
          <option value="all">Todos los estados</option>
          {STATUSES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
          {Object.entries(AUX_STATUSES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select value={filterPublico} onChange={(e) => setFilterPublico(e.target.value)} style={selectStyle}>
          <option value="all">Todos los públicos</option>
          {Object.entries(PUBLICOS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Conteos rapidos */}
      <div style={{ display: "flex", gap: spacing[4], marginBottom: spacing[4], fontSize: typography.sizes.sm, color: colors.gray600 }}>
        <span><b style={{ color: colors.gray900, fontWeight: 400 }}>{counts.total}</b> totales</span>
        <span style={{ color: colors.primary }}><b style={{ fontWeight: 400 }}>{counts.new}</b> nuevos</span>
        <span style={{ color: colors.warning }}><b style={{ fontWeight: 400 }}>{counts.sinContactar}</b> sin contactar 24h+</span>
        <span style={{ color: colors.error }}><b style={{ fontWeight: 400 }}>{counts.needsHuman}</b> necesitan atención</span>
      </div>

      {/* Layout 2 paneles */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 1fr) minmax(360px, 2fr)", gap: spacing[4], minHeight: 500 }}>
        {/* Panel izq — lista */}
        <div style={panelStyle}>
          {filteredLeads.length === 0 ? (
            <div style={{ padding: spacing[4] }}>
              <EmptyState
                icon={FaUserPlus}
                title="Sin leads"
                description="Los leads de demos web y prospectos de WhatsApp aparecerán acá."
              />
            </div>
          ) : (
            filteredLeads.map((l) => {
              const isActive = l.id === selectedId;
              const status =
                STATUSES.find((s) => s.id === l.status) ||
                AUX_STATUSES[l.status] ||
                { label: l.status || "Nuevo", color: colors.primary };
              const source = SOURCES[l.source] || SOURCES.manual;
              const Icon = source.icon;
              return (
                <div
                  key={l.id}
                  onClick={() => setSelectedId(l.id)}
                  style={{
                    padding: spacing[3],
                    borderBottom: `1px solid ${colors.gray100}`,
                    background: isActive ? colors.primaryLighter : "transparent",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <Icon size={12} color={source.color} />
                      <span style={{ fontWeight: 400, color: colors.gray900, fontSize: typography.sizes.base, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {l.name || l.email || l.phone || "Sin nombre"}
                      </span>
                    </div>
                    {l.needsHuman && (
                      <FaExclamationCircle size={12} color={colors.error} title="Necesita atención" />
                    )}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: typography.sizes.sm, color: colors.gray600 }}>
                    <span>
                      {PUBLICOS[l.publico] || ROLES[l.rol] || l.rol || (l.email || l.phone || "—")}
                    </span>
                    <span style={{ color: status.color, fontWeight: 400 }}>{status.label}</span>
                  </div>
                  {(() => {
                    const h = horasSinContactar(l);
                    if (h === null || h < 24) return null;
                    return (
                      <div style={{ fontSize: typography.sizes.sm, color: colors.error }}>
                        Sin contactar hace {h >= 48 ? `${Math.floor(h / 24)} días` : `${h}h`}
                      </div>
                    );
                  })()}
                </div>
              );
            })
          )}
        </div>

        {/* Panel der — detalle */}
        <div style={panelStyle}>
          {!selected ? (
            <div style={{ padding: spacing[4] }}>
              <EmptyState
                icon={FaUserPlus}
                title="Seleccioná un lead"
                description="Hacé clic en un lead a la izquierda para ver el detalle."
              />
            </div>
          ) : (
            <LeadDetail
              lead={selected}
              onChangeStatus={changeStatus}
              onAddNote={addNote}
              onAssign={assignLead}
              currentEmail={auth.currentUser?.email || ""}
              newNote={newNote}
              setNewNote={setNewNote}
              updating={updating}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Subcomponente: detalle de un lead
const LeadDetail = ({ lead, onChangeStatus, onAddNote, onAssign, currentEmail, newNote, setNewNote, updating }) => {
  const source = SOURCES[lead.source] || SOURCES.manual;
  const SourceIcon = source.icon;
  const slaHoras = horasSinContactar(lead);

  const fmtDate = (ts) => {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div style={{ padding: spacing[4], display: "flex", flexDirection: "column", gap: spacing[4] }}>
      {/* Cabecera */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <SourceIcon size={14} color={source.color} />
          <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>{source.label}</span>
        </div>
        <h2 style={{ fontSize: typography.sizes.xl, fontWeight: 400, color: colors.gray900, margin: 0 }}>
          {lead.name || lead.email || lead.phone || "Sin nombre"}
        </h2>
        {lead.rol && (
          <p style={{ fontSize: typography.sizes.base, color: colors.gray600, margin: "4px 0 0", fontWeight: 400 }}>
            {ROLES[lead.rol] || lead.rol}
          </p>
        )}
      </div>

      {/* SLA + asignacion */}
      {slaHoras !== null && slaHoras >= 24 && (
        <div
          style={{
            padding: spacing[2],
            background: colors.errorLight,
            borderRadius: radius.sm,
            fontSize: typography.sizes.sm,
            color: colors.errorDark,
          }}
        >
          Sin contactar hace {slaHoras >= 48 ? `${Math.floor(slaHoras / 24)} días` : `${slaHoras} horas`} — respondele desde el WhatsApp comercial.
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: typography.sizes.sm }}>
        <span style={{ color: colors.gray600, minWidth: 90 }}>Dueño:</span>
        <span style={{ color: lead.asignadoEmail ? colors.gray900 : colors.gray400 }}>
          {lead.asignadoEmail || "sin asignar"}
        </span>
        {lead.asignadoEmail !== currentEmail && (
          <button
            onClick={() => onAssign(currentEmail)}
            disabled={updating}
            className="habitia-btn-secondary"
            style={asignarBtnStyle}
          >
            Tomarlo yo
          </button>
        )}
        {lead.asignadoEmail && (
          <button
            onClick={() => onAssign("")}
            disabled={updating}
            className="habitia-btn-ghost"
            style={{ ...asignarBtnStyle, border: "none", color: colors.gray600 }}
          >
            Liberar
          </button>
        )}
      </div>

      {/* Datos */}
      <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
        {lead.publico && <Field icon={FaUser} label="Público" value={PUBLICOS[lead.publico] || lead.publico} />}
        {lead.email && <Field icon={FaEnvelope} label="Email" value={lead.email} />}
        {lead.phone && <Field icon={FaPhone} label="Teléfono" value={lead.phone} />}
        {lead.edificio && <Field icon={FaBuilding} label="Edificio" value={lead.edificio} />}
        {lead.ubicacion && <Field icon={FaBuilding} label="Ubicación" value={lead.ubicacion} />}
        {typeof lead.quoteAmount === "number" && (
          <Field
            icon={FaTrophy}
            label="Cotización"
            value={`$ ${Math.round(lead.quoteAmount).toLocaleString("es-UY")} / mes`}
          />
        )}
        {lead.quotePdfUrl && (
          <Field
            icon={FaFilePdf}
            label="PDF"
            value={
              <a
                href={lead.quotePdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: colors.primary }}
              >
                Ver cotización enviada
              </a>
            }
          />
        )}
        {(lead.quoteWhatsappSent || lead.quoteEmailSent) && (
          <Field
            icon={FaWhatsapp}
            label="Enviada por"
            value={[lead.quoteWhatsappSent && "WhatsApp", lead.quoteEmailSent && "Email"]
              .filter(Boolean)
              .join(" + ")}
          />
        )}
        {lead.contactedAt && (
          <Field
            icon={FaPhone}
            label="Contactado"
            value={`${fmtDate(lead.contactedAt)}${lead.contactedBy ? ` por ${lead.contactedBy}` : ""}`}
          />
        )}
        {lead.firstMessage && (
          <Field icon={FaEnvelope} label="Mensaje" value={`"${lead.firstMessage}"`} />
        )}
        {lead.demoUso && (
          <Field
            icon={FaUser}
            label="Uso de demo"
            value={`${lead.demoUso.mensajesUsuario ?? lead.demoUso.mensajesHabi ?? 0} mensajes a Habi${lead.demoUso.ultimaActividad ? ` · última actividad ${fmtDate(lead.demoUso.ultimaActividad)}` : ""}`}
          />
        )}
      </div>

      {/* Estado: chips de transicion */}
      <div>
        <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: 6 }}>
          Estado
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {STATUSES.map((s) => {
            const active = s.id === lead.status;
            return (
              <button
                key={s.id}
                onClick={() => onChangeStatus(s.id)}
                disabled={updating || active}
                style={{
                  padding: "6px 12px",
                  fontSize: typography.sizes.sm,
                  fontWeight: 400,
                  background: active ? s.color : colors.white,
                  color: active ? colors.white : s.color,
                  border: `1px solid ${s.color}`,
                  borderRadius: radius.full,
                  cursor: active ? "default" : "pointer",
                  opacity: updating ? 0.6 : 1,
                  fontFamily: "inherit",
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Banner de urgencia */}
      {lead.needsHuman && (
        <div
          style={{
            padding: spacing[3],
            background: colors.errorLight,
            borderRadius: radius.sm,
            borderLeft: `3px solid ${colors.error}`,
          }}
        >
          <div style={{ fontSize: typography.sizes.sm, fontWeight: 400, color: colors.errorDark, marginBottom: 4 }}>
            Necesita atención humana
          </div>
          {lead.escalationReason && (
            <div style={{ fontSize: typography.sizes.sm, color: colors.gray700 }}>
              Motivo: {lead.escalationReason}
            </div>
          )}
          {lead.escalatedAt && (
            <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
              Escalado: {fmtDate(lead.escalatedAt)}
            </div>
          )}
        </div>
      )}

      {/* Notas */}
      <div>
        <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: 6 }}>
          Notas internas
        </div>
        {(lead.notes || []).length === 0 && (
          <div style={{ fontSize: typography.sizes.sm, color: colors.gray400, fontStyle: "italic", marginBottom: 8 }}>
            Sin notas aún.
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
          {(lead.notes || []).map((n, i) => (
            <div
              key={i}
              style={{
                padding: spacing[2],
                background: colors.gray50,
                borderRadius: radius.sm,
                fontSize: typography.sizes.sm,
              }}
            >
              <div style={{ color: colors.gray900 }}>{n.text}</div>
              <div style={{ fontSize: 11, color: colors.gray600, marginTop: 2 }}>
                {n.by || "—"} · {fmtDate(n.at)}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            type="text"
            placeholder="Agregar nota..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onAddNote();
            }}
            style={{
              flex: 1,
              padding: "8px 10px",
              border: `1px solid ${colors.gray300}`,
              borderRadius: radius.sm,
              fontFamily: "inherit",
              fontSize: typography.sizes.sm,
              fontWeight: 400,
            }}
          />
          <button
            onClick={onAddNote}
            disabled={!newNote.trim() || updating}
            style={{
              padding: "8px 16px",
              background: colors.primary,
              color: colors.white,
              border: "none",
              borderRadius: radius.sm,
              cursor: !newNote.trim() || updating ? "default" : "pointer",
              fontFamily: "inherit",
              fontSize: typography.sizes.sm,
              fontWeight: 400,
              opacity: !newNote.trim() || updating ? 0.5 : 1,
            }}
          >
            Agregar
          </button>
        </div>
      </div>

      {/* Timestamps */}
      <div
        style={{
          fontSize: typography.sizes.sm,
          color: colors.gray600,
          paddingTop: spacing[2],
          borderTop: `1px solid ${colors.gray100}`,
        }}
      >
        Creado: {fmtDate(lead.createdAt)} · Última actividad: {fmtDate(lead.updatedAt || lead.lastActivityAt)}
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, value }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: typography.sizes.base }}>
    <Icon size={12} color={colors.gray600} />
    <span style={{ color: colors.gray600, minWidth: 90, fontSize: typography.sizes.sm }}>{label}:</span>
    <span style={{ color: colors.gray900, fontWeight: 400 }}>{value}</span>
  </div>
);

const selectStyle = {
  padding: "8px 12px",
  border: `1px solid ${colors.gray300}`,
  borderRadius: radius.sm,
  fontFamily: "inherit",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  background: colors.white,
};

const asignarBtnStyle = {
  padding: "4px 10px",
  fontSize: typography.sizes.sm,
  fontWeight: 400,
  background: colors.white,
  color: colors.gray900,
  border: `1px solid ${colors.gray200}`,
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "all 0.15s ease",
};

const panelStyle = {
  background: colors.white,
  border: `1px solid ${colors.gray200}`,
  borderRadius: radius.md,
  overflow: "auto",
  maxHeight: 700,
};

export default Leads;
